import React, { useState, useEffect } from 'react';
import './PositionLayout.css';
import { UnmemoisedInstance } from 'cdux_common';
import { CloseInstanceButton } from 'cdux_common';
import { useSession } from '../../Utils/SessionProvider';
import { checkReadOnly } from '../../Utils/ReadOnlyElements';
import { DownloadButton } from 'cdux_common';
import { Fallback } from 'cdux_common';
import { useTranslate } from 'react-translate';
import { InstanceBuilder, Transform } from '../../models/Instance';

export function PositionLayout(props) {
    const [rootInstances, setRootInstances] = useState(props.instances.filter(i => i.id !== "background"))
    const [zIndices, setZIndices] = useState(() => {
        const initial = {};
        props.instances.forEach(instance => {
            const key =  instance.id;
            initial[key] = 0;
        });
        return initial;
    });

    const [instances, setInstances] = useState([]);

    const BOX_PADDING = 12;

    const boundingBox = {
        top: 0 + BOX_PADDING,
        right: window.innerWidth - BOX_PADDING,
        bottom: window.innerHeight - BOX_PADDING,
        left: 0 + BOX_PADDING
    }

    const session = useSession();

    const t = useTranslate("viewer");

    const isReadOnly = checkReadOnly();

    function getLoadingMessage(instance) {
        switch (instance.itemType) {
            case "pdf":
                return t('loadPdf');
            default: return t('loadDocument');

        }
    }

    function checkBoundsOnDrag(clickX, clickY, event, instance) {

        if ((event.clientX + (instance.width / 2)) > boundingBox.right) {

            const rightEdge = event.clientX + (instance.width / 2);
            const overflow = rightEdge - boundingBox.right;
            clickX = event.clientX - overflow;

        } else if ((event.clientX - (instance.width / 2)) < boundingBox.left) {

            const leftEdge = event.clientX - (instance.width / 2);
            const overflow = boundingBox.left - leftEdge;
            clickX = event.clientX + overflow;
        }

        if ((event.clientY - (instance.height / 2)) < boundingBox.top) {

            const topEdge = event.clientY - (instance.height / 2);
            const overflow = boundingBox.top - topEdge;
            clickY = event.clientY + overflow;

        } else if ((event.clientY + (instance.height / 2)) > boundingBox.bottom) {

            const bottomEdge = event.clientY + (instance.height / 2);
            const overflow = bottomEdge - boundingBox.bottom;
            clickY = event.clientY - overflow;
        }

        return { clickX, clickY };
    }

    function getinstancePosition(event) {

        let positionX;
        let positionY
        if (!event.clientX && !event.clientY) {
            positionX = 0.5;
            positionY = 0.5;
        } else {
            positionX = event.clientX / window.innerWidth;
            positionY = event.clientY / window.innerHeight;
        } 

        return { positionX, positionY }
    }

    function handleDragStart(instanceKey) {
        setZIndices(prev => {
            const updated = { ...prev };
            const clickedZ = updated[instanceKey] ?? 0;

            for (let key in updated) {
                if (updated[key] > clickedZ) {
                    updated[key] -= 1;
                }
            }

            updated[instanceKey] = Object.keys(updated).length;
            return updated;
        });
    }

    function dragOpenedInstanceToPointer(alreadyOpen, event) {
        const target = document.getElementById(`layout-${alreadyOpen.id}`);
        const targetScaled = document.getElementById(alreadyOpen.id).getBoundingClientRect();

        let clickX = event.clientX
        let clickY = event.clientY

        const clicks = checkBoundsOnDrag(clickX, clickY, event, targetScaled);

        clickX = clicks.clickX;
        clickY = clicks.clickY;

        if (target) {
            const x = clickX / window.innerWidth * 100;
            const y = clickY / window.innerHeight * 100;
            target.style.transform = `translate(${x}%, ${y}%)`;
            target.setAttribute('data-x', x);
            target.setAttribute('data-y', y);

            target.style.transition = 'transform 0.5s ease';

            target.addEventListener('transitionend', () => {
                target.style.transition = '';
            }, { once: true });
        }
    }

    function handleAnnotation(args) {
        let section;
        if (args.sections) {
            section = args.sections.find((section) => section.Pages.includes(args.destination - 1))

            if (section) {
                const parentSection = args.sections.find((section) => section.Pages[0] === 0);

                let instance = props.instances.find(instance => instance.id === parentSection.Id);

                openInstance(section.Id, instance, args.event)
            }

        } else if (args.annotation.fieldName) {
            const cleanedLinkedInstance = args.annotation.fieldName.replace(/^cdux-/, '');
                const instances = [
                    ...session.instances,
                    ...(session.linkedInstances ?? [])
                ];
            const instance = instances.find(instance => instance.id === cleanedLinkedInstance);

            if (instance) {
                openInstance(instance.id, instance, args.event);
                    }
        }
    }

    function openInstance(id, instance, event) {

        const { positionX, positionY } = getinstancePosition(event);

        const transform = new Transform(
            positionX,
            positionY,
            window.innerWidth,
            window.innerHeight,
            0.5
        )
 

        const newInstance = new InstanceBuilder()
            .setId(id)
            .setCanBeEmbedded(instance.canBeEmbedded)
            .setChildrenOverflow(instance.childrenOverflow)
            .setCurrentPageId(instance.currentPageId)
            .setfileName(instance.fileName)
            .setHasRoundedCorner(instance.hasRoundedCorner)
            .setInteractivePages(instance.interactivePages)
            .setItemType(instance.itemType)
            .setNaturalOrder(instance.naturalOrder)
            .setPages(instance.pages)
            .setPosition(instance.position)
            .setpublicWebUrl(instance.publicWebUrl)
            .setReferenceData(instance.referenceData)
            .setThumbnailSize(instance.thumbnailSize)
            .setThumbnailUrl(instance.thumbnailUrl)
            .setTransform(transform)
            .build()

        setZIndices(prev => {
            const updated = { ...prev };

            if (!(newInstance.id in updated)) {
                updated[newInstance.id] = 0;
            }
            return updated;
        });

        setInstances(prevList => {
            const alreadyOpen =
                prevList.find(i => i.id === newInstance.id) ||
                props.instances.find(i => i.id === newInstance.id);

            if (alreadyOpen) {

                dragOpenedInstanceToPointer(alreadyOpen, event);

                return prevList;
            } else {
                return [...prevList, newInstance]
            }

        });

        handleDragStart(newInstance.id);
    }

    function closeInstance(instanceToClose) {

        setInstances(prevList =>
                prevList.filter(instance => instance.id !== instanceToClose.id)
            );
            
    }

    useEffect(() => {

        if (props.interactiveBackgroundArgs) {
            handleAnnotation(props.interactiveBackgroundArgs)
        }

    }, [props.interactiveBackgroundArgs])

    return (
        <div className="positionPanel" id="positionPanel"
            style={{
                width: `${Math.floor(props.workspace.width)}px`,
                height: `${Math.floor(props.workspace.height)}px`
            }}>
            {rootInstances.map((i, index) => {
                return (
                    <UnmemoisedInstance
                        key={'pos-thumbnail-' + i.id}
                        index={index}
                        workspace={session.workspace}
                        theme={session.theme}
                        instance={i}
                        url={i.publicWebUrl}
                        isReadOnly={isReadOnly}
                        onIndexChange={() => handleDragStart(i.id)}
                        zIndex={zIndices[i.id]}
                        canBeMoved={true}
                        handleAnnotation={handleAnnotation}
                        boundingBox={boundingBox}
                        // hasInertia
                        buttons={{
                            expand: 'expand',
                            mainAction: 'mainAction',
                            download: <DownloadButton instance={i} target={i.publicWebUrl} onClick={props.downloadFile} theme={ session.theme} />
                        }} 
                        Fallback={<Fallback
                            workspace={session.workspace}
                            theme={session.theme}
                            thumbnailUrl={i.thumbnailUrl}
                            message={i.itemType === "web" ? t('iframeFallbackMessage') : t('unsupported-message')}
                            button={<a className="button" href={i.publicWebUrl} target="_blank" rel="noreferrer" download>{i.itemType === "web" ? t('iframeFallbackBtn') : t('download-label')}</a> }
                        />}
                        loadingMessage={getLoadingMessage(i) }
                        appearDelay={true }
                        useThumbnail={true }
                    />
                );
            })}
            {instances.map((i, index) => {
                return (
                    <UnmemoisedInstance
                        key={'linkedinstance-pos-thumbnail-' + i.id}
                        index={index}
                        workspace={session.workspace}
                        theme={session.theme}
                        instance={i}
                        url={i.publicWebUrl}
                        isReadOnly={isReadOnly}
                        onIndexChange={() => handleDragStart(i.id)}
                        zIndex={zIndices[i.id]}
                        canBeMoved={true}
                        handleAnnotation={handleAnnotation}
                        boundingBox={boundingBox}
                        // hasInertia
                        // canBounce
                        // canFade
                        closeInstance={closeInstance}
                        buttons={{
                            close: < CloseInstanceButton theme={session.theme} onClick={() => closeInstance(i)} />,
                            expand: 'expand',
                            mainAction: 'mainAction',
                            download: <DownloadButton instance={i} target={i.publicWebUrl} onClick={props.downloadFile} theme={session.theme} />
                        }}
                        Fallback={<Fallback
                            workspace={session.workspace}
                            theme={session.theme}
                            thumbnailUrl={i.thumbnailUrl}
                            message={i.itemType === "web" ? t('iframeFallbackMessage') : t('unsupported-message')}
                            button={<a className="button" href={i.publicWebUrl} target="_blank" rel="noreferrer" download>{i.itemType === "web" ? t('iframeFallbackBtn') : t('download-label')}</a>}
                        />}
                        loadingMessage={getLoadingMessage(i)}
                    />
                );
            })}

        </div>
    );
}
